<template>
    <vx-modal
        v-model:visible="visible"
        :preventClosing="props.hasTakenEffect"
        data-test="terms-owner-modal"
        padding="standard"
        size="lg"
    >
        <template v-slot:header>{{ props.pendingTerm.title }}</template>

        <vx-alert data-test="terms-pending-owner-alert" class="mb-10" color="info" :closeable="false" :shadow="true">
            <div class="font-extrabold text-base mb-1">Action required</div>
            <div v-if="props.hasTakenEffect">
                New Terms &amp; Conditions are now in effect. You as the group Owner must accept these terms to restore
                service.
            </div>
            <div v-else>
                New Terms &amp; Conditions take effect on <strong>{{ props.formattedEffectiveAt }}</strong
                >. You as the group Owner must accept these terms to avoid interruptions in service.
            </div>
        </vx-alert>

        <markdown-renderer :source="props.pendingTerm.content" />
        <div class="mt-6 bg-white rounded p-4 sticky left-0 bottom-0 shadow text-slate-800 flex items-start gap-3">
            <vx-checkbox v-model="consentChecked" data-test="terms-consent-checkbox" />
            <label class="text-sm cursor-pointer" @click="consentChecked = !consentChecked">
                {{ props.pendingTerm.consent_text }}
            </label>
        </div>
        <template v-slot:footer>
            <div class="flex justify-end gap-2">
                <vx-button
                    v-if="!props.hasTakenEffect"
                    data-test="terms-cancel-button"
                    size="lg"
                    color="muted"
                    @click="visible = false"
                    >Cancel
                    <font-awesome-icon :icon="faTimesCircle"></font-awesome-icon>
                </vx-button>
                <vx-button v-if="props.hasTakenEffect" color="muted" size="lg" href="mailto:support@voxie.com">
                    Contact Support <font-awesome-icon :icon="faEnvelope"></font-awesome-icon>
                </vx-button>
                <vx-popover arrow placement="top" hover class="grow">
                    <vx-button
                        block
                        color="primary"
                        :disabled="!consentChecked"
                        :loading="submitting"
                        size="lg"
                        data-test="terms-submit-button"
                        @click="submit"
                    >
                        {{ props.pendingTerm.consent_button }}
                        <font-awesome-icon :icon="faCheckCircle"></font-awesome-icon>
                    </vx-button>
                    <template v-if="!consentChecked" v-slot:content
                        >You must check "{{ props.pendingTerm.consent_text }}" before proceeding</template
                    >
                </vx-popover>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { VxAlert, VxButton, VxCheckbox, VxModal, VxPopover } from '@voxie/frontend-components';
import MarkdownRenderer from '~/components/general/MarkdownRenderer.vue';
import termsClient from '~/services/termsClient';
import { useToasted } from '~/composables/useToasted';
import type { Term } from '~/services/termsClient.types';
import { catchRequestError } from '~/utils/request';
import { useCallout } from '~/composables/useCallout';
import { faCheckCircle, faEnvelope, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const callout = useCallout();
const toasted = useToasted();

const teamId = Spark.state.currentTeam?.id ?? null;
const groupId = Spark.state.currentGroup?.group_id ?? null;

const visible = defineModel<boolean>('visible');

const props = defineProps<{
    pendingTerm: Term;
    hasTakenEffect: boolean;
    formattedEffectiveAt: string;
}>();

const emit = defineEmits(['accepted']);

const consentChecked = ref(false);
const submitting = ref(false);

const submit = async () => {
    if (!props.pendingTerm || !teamId || !groupId || !consentChecked.value) {
        return;
    }

    submitting.value = true;

    try {
        await termsClient.acceptTerm(teamId, groupId, props.pendingTerm.id);
        visible.value = false;
        consentChecked.value = false;
        toasted.success('Terms & Conditions accepted successfully');
        if (Spark.state.currentGroup?.is_billable) {
            callout.danger(
                'group-billing',
                '<strong>ADDITIONAL ACTION REQUIRED:</strong> Enter billing email, address, and credit card information below. Account may be suspended for missing billing information.',
                {
                    ttl: 30,
                    action: Spark.stripeKey
                        ? { label: 'Add Payment Method', scrollTo: '#group-payment-method' }
                        : undefined,
                }
            );
            // callouts render newest-first, so the success alert shows above the action-required one
            callout.success('group-billing', 'Terms & Conditions successfully accepted!', { ttl: 30 });

            window.location.assign('/settings/group-billing#/billing-details');
        }
        emit('accepted');
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        submitting.value = false;
    }
};
</script>
